import React, { FC, ReactElement, useCallback, useEffect, useState } from 'react';
import { Badge, Button, Form } from 'react-bootstrap';
import { CalendarPlus } from 'lucide-react';
import i18next from 'i18next';
import { NavigateFunction, useNavigate } from 'react-router';
import { DateTime } from 'luxon';
import ConfirmationModal from '../../components/ConfirmationModal';
import SecondaryButton from '../../components/SecondaryButton';
import { formatDate, formatTime, toDateTime } from '../../util/date-utils';
import { BlobResult, downloadBlob, isNotifiedError, useAuthFetchFromRestAPI } from '../../util/api';
import { emitErrorNotification } from '../Notification';
import { AppointmentDto, AppointmentPatientDto } from '../../../../shared/dtos/appointment.dto';
import { AppointmentStatus } from '../../../../shared/utils/enums';

type AppointmentListProps = {
  appointments: AppointmentDto[];
}

const variantByStatus: Record<AppointmentStatus, string> = {
  [AppointmentStatus.PENDING]: 'badge-bg-secondary',
  [AppointmentStatus.CONFIRMED]: 'badge-bg-info',
  [AppointmentStatus.THERAPIST_CANCELLED]: 'badge-bg-danger',
  [AppointmentStatus.PATIENT_CANCELLED]: 'badge-bg-danger',
  [AppointmentStatus.REJECTED]: 'badge-bg-danger',
  [AppointmentStatus.REQUESTED]: 'badge-bg-secondary',
  [AppointmentStatus.COMPLETED]: '',
  [AppointmentStatus.DELETED]: '',
};

const getPatientName = (patient?: AppointmentPatientDto): string => {
  if (!patient) {
    return '-';
  }
  
  return `${patient.firstName ?? '-'} ${patient.lastName ?? '-'}`;
};

const AppointmentList: FC<AppointmentListProps> = ({ appointments }: AppointmentListProps): ReactElement => {
  const [appointmentId, setAppointmentId] = useState<string>();
  const [comment, setComment] = useState('');
  const [showCancelModel, setShowCancelModal] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [list, setList] = useState<AppointmentDto[]>([]);
  const [busy, setBusy] = useState<null | 'cancel' | 'accept' | 'reject'>(null);
  const [downloadingIcsId, setDownloadingIcsId] = useState<string | null>(null);

  const navigate: NavigateFunction = useNavigate();
  const { authFetch, authFetchBlob } = useAuthFetchFromRestAPI();

  useEffect(() => {
    setList(appointments);
  }, [appointments]);

  const patchLocalStatus = useCallback((id: string, status: AppointmentStatus) => {
    setList((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
  }, []);

  const handleCancel = useCallback(async () => {
    if (!appointmentId) {
      return;
    }

    setBusy('cancel');

    try {
      const result: AppointmentDto = await authFetch(`/api/v1/appointment/therapist/cancel/${appointmentId}`, {
        method: 'POST',
        body: { comment },
      });
      patchLocalStatus(appointmentId, result.status);
      setComment('');
    } catch (error) {
      console.error('Error cancel appointment', error);
    } finally {
      setBusy(null);
      setShowCancelModal(false);
    }
  }, [appointmentId, comment, authFetch, patchLocalStatus]);

  const handleAccept = useCallback(async () => {
    if (!appointmentId) {
      return;
    }

    setBusy('accept');

    try {
      const result: AppointmentDto = await authFetch(`/api/v1/appointment/therapist/confirm/${appointmentId}`, {
        method: 'POST',
      });
      patchLocalStatus(appointmentId, result.status);
    } catch (error) {
      console.error('Error accept appointment', error);
    } finally {
      setBusy(null);
      setShowAcceptModal(false);
    }
  }, [appointmentId, authFetch, patchLocalStatus]);

  const handleReject = useCallback(async () => {
    if (!appointmentId) {
      return;
    }

    setBusy('reject');

    try {
      const result: AppointmentDto = await authFetch(`/api/v1/appointment/therapist/reject/${appointmentId}`, {
        method: 'POST',
        body: { comment },
      });
      patchLocalStatus(appointmentId, result.status);
      setComment('');
    } catch (error) {
      console.error('Error reject appointment', error);
    } finally {
      setBusy(null);
      setShowRejectModal(false);
    }
  }, [appointmentId, comment, authFetch, patchLocalStatus]);

  const handleDownloadIcs = useCallback(async (id: string) => {
    setDownloadingIcsId(id);

    try {
      const result: BlobResult = await authFetchBlob(`/api/v1/appointment/${id}/ics`);
      downloadBlob(result.blob, result.fileName);
    } catch (error) {
      console.error('Error downloading appointment ics', error);

      if (!isNotifiedError(error)) {
        emitErrorNotification(i18next.t('errorMessageTemplate.genericMsg'));
      }
    } finally {
      setDownloadingIcsId(null);
    }
  }, [authFetchBlob]);

  const appointmentStatusBadge = (status: AppointmentStatus): ReactElement => (
    <Badge className={`float-start badge-status align-top me-3 w-105 ${variantByStatus[status]}`}>
      {i18next.t(`createAppointmentForm.appointmentStatus.${status}`)}
    </Badge>
  );
  
  return (
    <main className="d-flex flex-grow-1 flex-column">
      <ConfirmationModal
        show={showCancelModel}
        title={i18next.t('createAppointmentForm.deleteModal.title')}
        body={
          <>
            <p>{i18next.t('createAppointmentForm.deleteModal.body')}</p>
            <Form.Control
              as="textarea"
              rows={3}
              maxLength={2000}
              value={comment}
              onChange={(event) => setComment(event.target.value.slice(0, 2000))}
            />
            <div className="text-end small mt-1">{comment.length}/2000</div>
          </>
        }
        buttonPositive={i18next.t('createAppointmentForm.labels.delete')}
        buttonNegative={i18next.t('generic.cancel')}
        onPositive={handleCancel}
        onNegative={() => setShowCancelModal(false)}
        disableButton={busy === 'cancel'}
      />

      <ConfirmationModal
        show={showAcceptModal}
        title={i18next.t('createAppointmentForm.acceptModal.title')}
        body={<p>{i18next.t('createAppointmentForm.acceptModal.body')}</p>}
        buttonPositive={i18next.t('createAppointmentForm.labels.accept')}
        buttonNegative={i18next.t('generic.cancel')}
        onPositive={handleAccept}
        onNegative={() => setShowAcceptModal(false)}
        disableButton={busy === 'accept'}
      />

      <ConfirmationModal
        show={showRejectModal}
        title={i18next.t('createAppointmentForm.rejectModal.title')}
        body={
          <>
            <p>{i18next.t('createAppointmentForm.rejectModal.body')}</p>
            <Form.Control
              as="textarea"
              rows={3}
              maxLength={2000}
              value={comment}
              onChange={(event) => setComment(event.target.value.slice(0, 2000))}
            />
            <div className="text-end small mt-1">{comment.length}/2000</div>
          </>
        }
        buttonPositive={i18next.t('createAppointmentForm.labels.reject')}
        buttonNegative={i18next.t('generic.cancel')}
        onPositive={handleReject}
        onNegative={() => setShowRejectModal(false)}
        disableButton={busy === 'reject'}
      />

      <div className="d-flex flex-column gap-2 mt-2">
        {list.length > 0 &&
          list
            .filter((appointment: AppointmentDto): boolean =>
              appointment.status !== undefined &&
              DateTime.now() < toDateTime(appointment.endDateTime).plus({ minutes: 30 })
            )
            .map((appointment: AppointmentDto): ReactElement => {
              const date: string = formatDate(appointment.startDateTime);
              const startTime: string = formatTime(appointment.startDateTime);
              const endTime: string = formatTime(appointment.endDateTime);

              const status: AppointmentStatus = appointment.status;

              const canAct: boolean =
                status !== AppointmentStatus.REJECTED &&
                status !== AppointmentStatus.THERAPIST_CANCELLED &&
                status !== AppointmentStatus.PATIENT_CANCELLED;

              const canCancel: boolean = DateTime.now() < toDateTime(appointment.startDateTime);

              const canEnter: boolean =
                DateTime.now() >= toDateTime(appointment.startDateTime).minus({ minutes: 15 }) &&
                DateTime.now() <= toDateTime(appointment.endDateTime).plus({ minutes: 30 });

              return (
                <div
                  key={`request-${appointment.id}`}
                  className="d-flex flex-wrap align-items-center gap-3 bg-white border rounded-3 p-3"
                >
                  <div className="d-flex flex-column">
                    <span className="fw-semibold text-nowrap">{date}</span>
                    <span className="text-muted small text-nowrap">
                      <i className="far fa-clock me-1" aria-hidden="true" />
                      {startTime} – {endTime}
                    </span>
                  </div>

                  <div className="fw-bold text-nowrap">{getPatientName(appointment.patient)}</div>

                  <div>{appointmentStatusBadge(status)}</div>

                  <div className="ms-auto d-flex flex-wrap justify-content-end gap-2">
                    {canAct && (
                      <>
                        {(status === AppointmentStatus.PENDING || status === AppointmentStatus.CONFIRMED) && (
                          <Button
                            variant="outline-dark"
                            className="d-inline-flex align-items-center"
                            title={i18next.t('createAppointmentForm.labels.downloadIcs')}
                            aria-label={i18next.t('createAppointmentForm.labels.downloadIcs')}
                            onClick={() => void handleDownloadIcs(appointment.id)}
                            disabled={downloadingIcsId === appointment.id}
                          >
                            <CalendarPlus size={18} />
                          </Button>
                        )}

                        {status === AppointmentStatus.PENDING && (
                          <>
                            <Button
                              variant="primary"
                              className="text-white"
                              onClick={() => {
                                setAppointmentId(appointment.id);
                                setShowAcceptModal(true);
                              }}
                              disabled={busy !== null}
                            >
                              <i className="fas fa-check me-1" aria-hidden="true" /> {i18next.t('generic.approve')}
                            </Button>

                            <Button
                              variant="outline-dark"
                              onClick={() => {
                                setAppointmentId(appointment.id);
                                setShowRejectModal(true);
                              }}
                              disabled={busy !== null}
                            >
                              <i className="fa fa-times me-1" aria-hidden="true" /> {i18next.t('generic.reject')}
                            </Button>
                          </>
                        )}

                        {status === AppointmentStatus.CONFIRMED && (
                          <>
                            {canCancel && (
                              <Button
                                variant="outline-dark"
                                onClick={() => {
                                  setAppointmentId(appointment.id);
                                  setShowCancelModal(true);
                                }}
                                disabled={busy !== null}
                              >
                                <i className="fas fa-times me-1" aria-hidden="true" /> {i18next.t('createAppointmentForm.labels.cancel')}
                              </Button>
                            )}

                            {canEnter && (
                              <SecondaryButton onClick={() => navigate(`/call/${appointment.roomCode}`)}>
                                <i className="fas fa-video me-1" aria-hidden="true" /> {i18next.t('patients.enterRoom')}
                              </SecondaryButton>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
      </div>
    </main>
  );
};

export default AppointmentList;
